import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { getLayout, saveLayout } from "@/api/layouts";
import type {
  BreakpointKey,
  BreakpointLayouts,
  GridLayoutItem,
  SizeVariant,
  WidgetConfigMap,
  WidgetDefinition,
} from "./types";
import { BREAKPOINT_KEYS } from "./types";

type Layout = GridLayoutItem;

const SAVE_DEBOUNCE_MS = 500;

const BASE_COLS = 12;

function emptyLayouts(): BreakpointLayouts {
  return { "2xl": [], xl: [], lg: [], md: [], sm: [] };
}

function normalizeLayouts(raw: unknown): BreakpointLayouts | null {
  if (!raw || typeof raw !== "object") return null;
  const result = emptyLayouts();
  let hasAny = false;
  for (const key of BREAKPOINT_KEYS) {
    const value = (raw as Record<string, unknown>)[key];
    if (Array.isArray(value)) {
      hasAny = true;
      result[key] = value as Layout[];
    }
  }
  return hasAny ? result : null;
}

function normalizeConfig(raw: unknown): WidgetConfigMap {
  if (!raw || typeof raw !== "object") return {};
  const out: WidgetConfigMap = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (value && typeof value === "object") {
      const v = value as Record<string, unknown>;
      const sizeVariant =
        v.sizeVariant === "xs" ||
        v.sizeVariant === "s" ||
        v.sizeVariant === "m" ||
        v.sizeVariant === "l"
          ? (v.sizeVariant as SizeVariant)
          : "m";
      out[key] = { sizeVariant };
    }
  }
  return out;
}

export function resolveSize(
  def: WidgetDefinition,
  variant: SizeVariant,
): { w: number; h: number; variant: SizeVariant } {
  const pref = def.sizes[variant];
  if (pref) return { ...pref, variant };
  const fallbackVariant = def.defaultSize;
  if (fallbackVariant && def.sizes[fallbackVariant]) {
    return { ...def.sizes[fallbackVariant]!, variant: fallbackVariant };
  }
  if (def.sizes.m) return { ...def.sizes.m, variant: "m" };
  if (def.sizes.l) return { ...def.sizes.l, variant: "l" };
  if (def.sizes.s) return { ...def.sizes.s, variant: "s" };
  if (def.sizes.xs) return { ...def.sizes.xs, variant: "xs" };
  return { w: 3, h: 2, variant: "m" };
}

/**
 * Scale an M/S/XS/L widget size to a target column count.
 * - On very narrow grids (≤4 cols, i.e. mobile) every widget goes full-width stacked.
 * - L (declared as baseCols wide in 12-col reference) always fills the full row,
 *   so ultrawide monitors don't leave side gutters on hero widgets.
 * - Other sizes keep absolute widths — so a wide monitor fits MORE widgets per row
 *   instead of stretching the same few to fill space.
 */
export function scaleSizeForCols(
  w: number,
  h: number,
  targetCols: number,
): { w: number; h: number } {
  if (targetCols <= 4) return { w: targetCols, h };
  if (w >= BASE_COLS) return { w: targetCols, h };
  return { w: Math.max(1, Math.min(targetCols, w)), h };
}

/** First-fit 2D bin-packer. For each widget, scan rows top-down and columns
 *  left-right, placing it at the first cell where its bounding box doesn't
 *  collide. This lets a tall widget (e.g. LAST FLIGHT at h=4) have shorter
 *  widgets packed alongside it in the same rows, instead of leaving a dead
 *  rectangle next to it that the cursor-based packer produced. */
function findFirstFit(
  occupied: boolean[][],
  w: number,
  h: number,
  cols: number,
): { x: number; y: number } {
  for (let y = 0; ; y++) {
    while (occupied.length <= y + h) occupied.push(new Array(cols).fill(false));
    for (let x = 0; x + w <= cols; x++) {
      let fits = true;
      for (let dy = 0; dy < h && fits; dy++) {
        for (let dx = 0; dx < w; dx++) {
          if (occupied[y + dy][x + dx]) {
            fits = false;
            break;
          }
        }
      }
      if (fits) return { x, y };
    }
  }
}

function packLayoutForCols(
  activeWidgetTypes: string[],
  widgetDefs: Record<string, WidgetDefinition>,
  config: WidgetConfigMap,
  targetCols: number,
): Layout[] {
  const items: Layout[] = [];
  const occupied: boolean[][] = [];
  for (const type of activeWidgetTypes) {
    const def = widgetDefs[type];
    if (!def) continue;
    const variant = config[type]?.sizeVariant ?? def.defaultSize ?? "m";
    const base = resolveSize(def, variant);
    const { w, h } = scaleSizeForCols(base.w, base.h, targetCols);
    const { x, y } = findFirstFit(occupied, w, h, targetCols);
    items.push({ i: type, x, y, w, h });
    for (let dy = 0; dy < h; dy++) {
      for (let dx = 0; dx < w; dx++) {
        occupied[y + dy][x + dx] = true;
      }
    }
  }
  return items;
}

/** Detect whether a layout has a meaningful dead rectangle inside its
 *  occupied area — a symptom of a widget being shrunk without its neighbours
 *  compacting in to fill the space (e.g. LAST FLIGHT at M with nothing to
 *  its right). Triggers a repack when waste within the layout's bounding
 *  box exceeds 15%. */
function hasDeadSpace(items: Layout[], cols: number): boolean {
  if (items.length === 0) return false;
  const maxY = Math.max(...items.map((i) => i.y + i.h));
  const totalArea = items.reduce((sum, i) => sum + i.w * i.h, 0);
  const gridArea = maxY * cols;
  if (gridArea === 0) return false;
  return totalArea / gridArea < 0.85;
}

export function buildDefaultLayouts(
  activeWidgetTypes: string[],
  widgetDefs: Record<string, WidgetDefinition>,
  config: WidgetConfigMap,
  cols: Record<BreakpointKey, number>,
): BreakpointLayouts {
  const out = emptyLayouts();
  for (const bp of BREAKPOINT_KEYS) {
    out[bp] = packLayoutForCols(activeWidgetTypes, widgetDefs, config, cols[bp]);
  }
  return out;
}

/**
 * Fill breakpoints that the saved layout doesn't have yet by packing the
 * widgets (in the saved lg order) for each missing breakpoint. This is how
 * existing users who saved layouts under the old 3-breakpoint model get
 * sensible defaults at xl / 2xl without losing their curated lg layout.
 *
 * Also force-repacks the `sm` layout if it contains items narrower than the
 * sm column count — a telltale sign of the old auto-scaling strategy that
 * produced unusably tiny 1-col widgets on phones — and repacks xl / 2xl
 * layouts whose rightmost extent is narrower than the current column count
 * (i.e. the layout was saved against a smaller grid).
 */
function fillMissingBreakpoints(
  saved: BreakpointLayouts,
  widgetDefs: Record<string, WidgetDefinition>,
  config: WidgetConfigMap,
  cols: Record<BreakpointKey, number>,
): { layouts: BreakpointLayouts; addedAny: boolean } {
  const referenceOrder =
    saved.lg.length > 0
      ? saved.lg.map((item) => item.i)
      : saved.md.length > 0
        ? saved.md.map((item) => item.i)
        : saved.sm.map((item) => item.i);

  const merged: BreakpointLayouts = { ...saved };
  let addedAny = false;
  for (const bp of BREAKPOINT_KEYS) {
    const current = merged[bp];
    const missing = !current || current.length === 0;
    // At sm the grid is a single-column vertical stack, so any y-gap is
    // pure wasted space (visible to the user as a black strip between
    // widgets). Detect gaps by comparing the stack's total height to the
    // sum of item heights — any mismatch means at least one row is empty.
    const smNeedsRepack =
      bp === "sm" &&
      current &&
      current.length > 0 &&
      (current.some((item) => item.w < cols.sm) ||
        current.reduce((s, i) => s + i.h, 0) !==
          Math.max(...current.map((i) => i.y + i.h)));
    // Repack wide-screen layouts saved against the old column counts
    // (xl: 16, 2xl: 20). If the rightmost occupied column is short of the
    // current grid width, the user is seeing a dead stripe on the right.
    const wideNeedsRepack =
      (bp === "xl" || bp === "2xl") &&
      current &&
      current.length > 0 &&
      Math.max(...current.map((item) => item.x + item.w)) < cols[bp];
    // Repack any layout that has significant dead space inside its
    // bounding box — e.g. a widget shrunk from L to M leaving a 6-col-wide
    // empty rectangle alongside it that the cursor-based packer never
    // filled. The first-fit repack closes those gaps.
    const hasWaste =
      !missing && current && current.length > 0 && hasDeadSpace(current, cols[bp]);
    if (missing || smNeedsRepack || wideNeedsRepack || hasWaste) {
      merged[bp] = packLayoutForCols(referenceOrder, widgetDefs, config, cols[bp]);
      addedAny = true;
    }
  }
  return { layouts: merged, addedAny };
}

interface UseLayoutStateArgs {
  pageKey: string;
  widgetDefs: Record<string, WidgetDefinition>;
  defaultActiveTypes: string[];
  cols: Record<BreakpointKey, number>;
}

export function useLayoutState({
  pageKey,
  widgetDefs,
  defaultActiveTypes,
  cols,
}: UseLayoutStateArgs) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["layouts", pageKey],
    queryFn: () => getLayout(pageKey),
    staleTime: 60_000,
  });

  const [layouts, setLayouts] = useState<BreakpointLayouts | null>(null);
  const [activeTypes, setActiveTypes] = useState<string[] | null>(null);
  const [config, setConfig] = useState<WidgetConfigMap>({});
  const saveTimerRef = useRef<number | null>(null);
  const persistRef = useRef<
    ((layouts: BreakpointLayouts, config: WidgetConfigMap) => void) | null
  >(null);

  const defaultConfig = useMemo(() => {
    const out: WidgetConfigMap = {};
    for (const type of defaultActiveTypes) {
      const def = widgetDefs[type];
      if (!def) continue;
      const variant = resolveSize(def, def.defaultSize ?? "m").variant;
      out[type] = { sizeVariant: variant };
    }
    return out;
  }, [defaultActiveTypes, widgetDefs]);

  const defaultLayouts = useMemo(
    () => buildDefaultLayouts(defaultActiveTypes, widgetDefs, defaultConfig, cols),
    [defaultActiveTypes, widgetDefs, defaultConfig, cols],
  );

  useEffect(() => {
    if (isLoading) return;
    const savedLayouts = data ? normalizeLayouts(data.layout) : null;
    const savedConfig = normalizeConfig(data?.widget_config);
    if (savedLayouts) {
      const referenceItems =
        savedLayouts.lg.length > 0
          ? savedLayouts.lg
          : savedLayouts.md.length > 0
            ? savedLayouts.md
            : savedLayouts.sm;
      const savedTypes = referenceItems.map((item) => item.i);
      const presentTypes = savedTypes.filter((type) => widgetDefs[type]);
      // Ensure every active widget has a config entry
      const filledConfig: WidgetConfigMap = { ...savedConfig };
      for (const type of presentTypes) {
        if (!filledConfig[type]) {
          const def = widgetDefs[type];
          filledConfig[type] = { sizeVariant: def?.defaultSize ?? "m" };
        }
      }
      // Backfill xl / 2xl (and any other missing BP) for users who saved under
      // the old 3-breakpoint schema, so wide monitors get a sensible starting
      // arrangement immediately.
      const { layouts: merged, addedAny } = fillMissingBreakpoints(
        savedLayouts,
        widgetDefs,
        filledConfig,
        cols,
      );
      setLayouts(merged);
      setActiveTypes(presentTypes);
      setConfig(filledConfig);
      if (addedAny) {
        persistRef.current?.(merged, filledConfig);
      }
    } else {
      setLayouts(defaultLayouts);
      setActiveTypes(defaultActiveTypes);
      setConfig(defaultConfig);
    }
  }, [
    isLoading,
    data,
    defaultLayouts,
    defaultActiveTypes,
    defaultConfig,
    widgetDefs,
    cols,
  ]);

  const persist = useCallback(
    (nextLayouts: BreakpointLayouts, nextConfig: WidgetConfigMap) => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
      saveTimerRef.current = window.setTimeout(async () => {
        try {
          await saveLayout(pageKey, {
            layout: nextLayouts as unknown as Record<string, Layout[]>,
            widget_config: nextConfig,
          });
          queryClient.invalidateQueries({
            queryKey: ["layouts", pageKey],
            refetchType: "none",
          });
        } catch {
          /* swallow; retry on next change */
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [pageKey, queryClient],
  );

  // Expose persist for callers that live before its declaration (e.g. the
  // data-load effect that backfills xl / 2xl on first load).
  useEffect(() => {
    persistRef.current = persist;
  }, [persist]);

  const handleLayoutChange = useCallback(
    (_current: Layout[], all: Record<string, Layout[]>) => {
      const strip = (items: Layout[] | undefined): Layout[] =>
        (items ?? []).map(({ i, x, y, w, h, minW, minH, maxW, maxH }) => ({
          i,
          x,
          y,
          w,
          h,
          ...(minW !== undefined ? { minW } : {}),
          ...(minH !== undefined ? { minH } : {}),
          ...(maxW !== undefined ? { maxW } : {}),
          ...(maxH !== undefined ? { maxH } : {}),
        }));
      const normalized: BreakpointLayouts = emptyLayouts();
      for (const bp of BREAKPOINT_KEYS) {
        normalized[bp] = all[bp] ? strip(all[bp]) : layouts?.[bp] ?? [];
      }
      setLayouts(normalized);
      persist(normalized, config);
    },
    [layouts, config, persist],
  );

  const findFreeSpot = (
    existing: Layout[],
    w: number,
    h: number,
    targetCols: number,
  ): { x: number; y: number } => {
    const occupied: boolean[][] = [];
    for (const item of existing) {
      for (let dy = 0; dy < item.h; dy++) {
        while (occupied.length <= item.y + dy) {
          occupied.push(new Array(targetCols).fill(false));
        }
        for (let dx = 0; dx < item.w; dx++) {
          if (item.x + dx < targetCols) {
            occupied[item.y + dy][item.x + dx] = true;
          }
        }
      }
    }
    return findFirstFit(occupied, w, h, targetCols);
  };

  const addWidget = useCallback(
    (type: string) => {
      const def = widgetDefs[type];
      if (!def) return;
      if (activeTypes?.includes(type)) return;

      const variant = def.defaultSize ?? "m";
      const base = resolveSize(def, variant);

      const nextLayouts: BreakpointLayouts = emptyLayouts();
      for (const bp of BREAKPOINT_KEYS) {
        const existing = [...(layouts?.[bp] ?? [])];
        const { w, h } = scaleSizeForCols(base.w, base.h, cols[bp]);
        const spot = findFreeSpot(existing, w, h, cols[bp]);
        existing.push({ i: type, x: spot.x, y: spot.y, w, h });
        nextLayouts[bp] = existing;
      }

      const nextConfig: WidgetConfigMap = {
        ...config,
        [type]: { sizeVariant: variant },
      };
      setLayouts(nextLayouts);
      setActiveTypes([...(activeTypes ?? []), type]);
      setConfig(nextConfig);
      persist(nextLayouts, nextConfig);
    },
    [activeTypes, cols, layouts, config, persist, widgetDefs],
  );

  const removeWidget = useCallback(
    (type: string) => {
      const def = widgetDefs[type];
      if (def?.required) return;
      const nextLayouts: BreakpointLayouts = emptyLayouts();
      for (const bp of BREAKPOINT_KEYS) {
        nextLayouts[bp] = (layouts?.[bp] ?? []).filter(
          (item) => item.i !== type,
        );
      }
      const nextConfig = { ...config };
      delete nextConfig[type];
      setLayouts(nextLayouts);
      setActiveTypes((activeTypes ?? []).filter((t) => t !== type));
      setConfig(nextConfig);
      persist(nextLayouts, nextConfig);
    },
    [activeTypes, layouts, config, persist, widgetDefs],
  );

  const setWidgetSize = useCallback(
    (type: string, variant: SizeVariant) => {
      const def = widgetDefs[type];
      if (!def) return;
      const base = resolveSize(def, variant);
      const nextLayouts: BreakpointLayouts = emptyLayouts();
      for (const bp of BREAKPOINT_KEYS) {
        const { w, h } = scaleSizeForCols(base.w, base.h, cols[bp]);
        nextLayouts[bp] = (layouts?.[bp] ?? []).map((item) =>
          item.i === type ? { ...item, w, h } : item,
        );
      }
      const nextConfig: WidgetConfigMap = {
        ...config,
        [type]: { sizeVariant: base.variant },
      };
      setLayouts(nextLayouts);
      setConfig(nextConfig);
      persist(nextLayouts, nextConfig);
    },
    [cols, layouts, config, persist, widgetDefs],
  );

  const resetLayout = useCallback(() => {
    setLayouts(defaultLayouts);
    setActiveTypes(defaultActiveTypes);
    setConfig(defaultConfig);
    persist(defaultLayouts, defaultConfig);
  }, [defaultActiveTypes, defaultLayouts, defaultConfig, persist]);

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  return {
    layouts,
    activeTypes: activeTypes ?? [],
    config,
    isReady: !!layouts && !!activeTypes,
    handleLayoutChange,
    addWidget,
    removeWidget,
    setWidgetSize,
    resetLayout,
  };
}
